import type { ApiError } from '../api';

interface Props {
  error: ApiError;
}

// Map server error codes / status codes to user-friendly titles.
// Keep the messages deliberately vague about *why* a token is gone —
// the backend's §6 M6 guidance is that viewer 404s must not distinguish
// "unknown token" from "expired token" to avoid token-existence oracles.
function displayTitle(error: ApiError): string {
  if (error.status === 404) return 'File not found or expired';
  if (error.status === 410) return 'File is no longer available';
  if (error.status === 0 || error.code === 'NETWORK_ERROR') return 'Network error';
  if (error.status >= 500) return 'Server error';
  if (error.status >= 400) return 'Request error';
  return 'Something went wrong';
}

function displayHint(error: ApiError): string {
  if (error.status === 404) {
    return 'Ask whoever shared this link to re-share the file — links expire 24 hours after upload.';
  }
  if (error.status === 410) {
    return 'The file content was cleaned up. Ask whoever shared the link to upload again.';
  }
  if (error.status === 0 || error.code === 'NETWORK_ERROR') {
    return 'Check your connection and try reloading.';
  }
  return 'Try reloading the page. If the problem persists, contact support.';
}

export function ErrorPanel({ error }: Props) {
  return (
    <main
      className="flex h-full items-center justify-center bg-surface text-panelFg"
      role="alert"
    >
      <div className="max-w-md rounded-2xl border border-panel bg-panel/60 p-8 shadow-lg">
        <h1 className="mb-2 text-2xl font-semibold text-danger">{displayTitle(error)}</h1>
        <p className="mb-6 text-sm text-muted">{displayHint(error)}</p>
        <dl className="space-y-1 text-xs text-muted">
          <div className="flex gap-2">
            <dt className="font-mono">status</dt>
            <dd className="font-mono">{error.status}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-mono">code</dt>
            <dd className="font-mono">{error.code}</dd>
          </div>
          {error.correlationId ? (
            <div className="flex gap-2">
              <dt className="font-mono">correlation</dt>
              <dd className="font-mono">{error.correlationId}</dd>
            </div>
          ) : null}
        </dl>
      </div>
    </main>
  );
}
